import { useState } from 'react'
import moment from 'moment'
import {
  Card,
  Grid,
  Button,
  Modal,
  Anchor,
} from '@enterprise-ui/canvas-ui-react'
import EnterpriseIcon, { FixturesIcon } from '@enterprise-ui/icons'
import { ContentPlaceholder } from './ContentPlaceholder'

/**
 * Displays a list of product release notes with a "Read More" modal for detailed information.
 *
 * @component
 * @param {Object} props
 * @param {Array<Object>} props.productReleaseNotes - Array of product release note objects.
 * @param {string} props.productReleaseNotes[].release_feature_name - Name of the release feature.
 * @param {string} props.productReleaseNotes[].launch_date - Launch date of the release.
 * @param {string} props.productReleaseNotes[].application_name - Name of the application.
 * @param {string} props.productReleaseNotes[].release_short_description - Short description of the release (HTML string).
 * @param {string} [props.productReleaseNotes[].read_more_link] - Optional link for more details.
 * @returns {JSX.Element} The rendered product release content component.
 */
export const ProductReleaseContent = ({ productReleaseNotes }) => {
  const [isReadMoreModalOpen, setIsReadMoreModalOpen] = useState(false)
  const [releaseContent, setReleaseContent] = useState(null)
  const readReleaseContent = (releaseContent) => {
    setReleaseContent(releaseContent)
    setIsReadMoreModalOpen(true)
  }
  return (
    <>
      <Card className="hc-pa-md hc-bg-contrast-weak home-relase-cards">
        <p
          className="hc-fs-md font-color-default hc-lh-expanded hc-mt-dense font-weight-700"
          style={{ display: 'flex' }}
        >
          <EnterpriseIcon icon={FixturesIcon} className="hc-mr-dense" />{' '}
          <h3 className="hc-fs-md font-color-default hc-lh-expanded font-weight-700">
            What’s New?
          </h3>
        </p>
        {productReleaseNotes?.length === 0 ? (
          <ContentPlaceholder
            data-testid="placeholder-content-release"
            length={2}
          />
        ) : (
          <Grid.Container>
            {productReleaseNotes?.map((eachRelease, productIndex) => (
              <Grid.Item md={12} xs={12} key={productIndex}>
                <Card
                  className="hc-pa-md bg-default-contrast-weak font-color-default"
                  padding="dense"
                >
                  <Grid.Container>
                    <Grid.Item md={6}>
                      <h4 className="hc-fs-md font-color-default font-weight-500">
                        {eachRelease?.release_feature_name}
                      </h4>
                      <span className="hc-fs-xs product-relase-launched-date font-weight-500">
                        {`Released on ${moment(eachRelease?.launch_date).format('MMM D, YYYY')}`}
                      </span>
                    </Grid.Item>
                    <Grid.Item md={6} style={{ textAlign: 'end' }}>
                      <span className="hc-fs-min home-release-notes-app-names font-color-secondary font-weight-500">
                        {eachRelease?.application_name}
                      </span>
                    </Grid.Item>
                  </Grid.Container>
                  <h5
                    className="hc-fs-sm app-description font-color-default hc-mt-normal hc-mb-normal font-weight-400"
                    dangerouslySetInnerHTML={{
                      __html: eachRelease?.release_short_description?.substring(
                        0,
                        200,
                      ),
                    }}
                  />
                  <Button
                    as="button"
                    type="ghost"
                    onClick={(e) => readReleaseContent(eachRelease)}
                    className="hc-fs-sm hc-mt-normal hc-clr-interactive hc-pl-none font-weight-500"
                    data-testid="read-more-button"
                    aria-label="Read More Content"
                  >
                    Read More
                  </Button>
                </Card>
              </Grid.Item>
            ))}
          </Grid.Container>
        )}
      </Card>

      {/* View Release Notes when click on Read More button*/}
      <Modal
        headingText={releaseContent?.release_feature_name}
        isVisible={isReadMoreModalOpen}
        onRefuse={() => setIsReadMoreModalOpen(false)}
        scrollBehavior="contained"
        footer
        size="normal"
        className="release-content-modal"
      >
        <div style={{ padding: '0 24px' }}>
          <span className="hc-fs-md product-relase-launched-date font-weight-400 hc-clr-interactive">
            Launched on {releaseContent?.launch_date}
          </span>
          <p
            className="hc-fs-md hc-mt-normal app-description font-color-default font-weight-400"
            dangerouslySetInnerHTML={{
              __html: releaseContent?.release_short_description,
            }}
          />
          {releaseContent?.read_more_link && (
            <Anchor
              href={releaseContent?.read_more_link}
              target="_blank"
              data-testid="read-more-details"
              aria-label="More Details"
            >
              More Details
            </Anchor>
          )}
        </div>
      </Modal>
    </>
  )
}
